import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pesquisa } from '@eleg360/types';
import { pesquisasApi } from '@/lib/api/pesquisas';
import { candidatosApi } from '@/lib/api/candidatos';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { formatarData } from '@/lib/format';
import { exportarCsv } from '@/lib/csv';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';
import {
  atalhosDeBusca,
  avaliarCompatibilidade,
  compararResultados,
  filtrarPesquisas,
} from './comparacao';

const CONFIANCA: Array<{ v: 'alta' | 'media' | 'baixa'; l: string; chip: string }> = [
  { v: 'alta', l: 'Alta', chip: 'good' },
  { v: 'media', l: 'Média', chip: 'warn' },
  { v: 'baixa', l: 'Baixa', chip: 'crit' },
];

const chipConfianca = (c: string) => CONFIANCA.find((x) => x.v === c)?.chip || 'neutral';

const CORES = ['var(--eleg)', 'var(--warn)', 'var(--accent)', 'var(--good)', 'var(--crit)'];

type Resultado = { nome: string; valor: number };

export function PesquisasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();

  const [selecionada, setSelecionada] = useState<Pesquisa | null>(null);
  const [modal, setModal] = useState<'nova' | 'editar' | null>(null);
  const [consulta, setConsulta] = useState('');
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: pesquisas = [], isLoading } = useQuery({
    queryKey: ['pesquisas'],
    queryFn: () => pesquisasApi.list(token),
    enabled: !!token,
  });

  const { data: evolucao = [] } = useQuery({
    queryKey: ['pesquisas-evolucao'],
    queryFn: () => pesquisasApi.getEvolucao(token),
    enabled: !!token,
  });

  // Nomes dos candidatos para sugerir nos resultados
  const { data: candidatos = [] } = useQuery({
    queryKey: ['candidatos'],
    queryFn: () => candidatosApi.list(token),
    enabled: !!token,
  });

  const pct = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  /** Sinal explícito e menos tipográfico — "-3" e "−3" não se confundem na leitura. */
  const sinal = (d: number) => (d > 0 ? `+${pct(d)}` : d < 0 ? `−${pct(Math.abs(d))}` : '0');

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['pesquisas'] });
    queryClient.invalidateQueries({ queryKey: ['pesquisas-evolucao'] });
  };

  const criar = useMutation({
    mutationFn: (dados: any) => pesquisasApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      toast('Pesquisa cadastrada.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao cadastrar a pesquisa.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) =>
      pesquisasApi.update(id, dados, token),
    onSuccess: (p) => {
      invalidar();
      toast('Pesquisa atualizada.');
      setSelecionada(p);
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a pesquisa.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => pesquisasApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Pesquisa removida.');
      setSelecionada(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover a pesquisa.')),
  });

  // Busca livre LOCAL: `GET /eleg/pesquisas` não aceita filtro de texto, então
  // o filtro roda sobre a lista já baixada — igual ao protótipo, que filtra o
  // array `PESQUISAS` em memória (proto:1770).
  const visiveis = filtrarPesquisas(pesquisas, consulta);
  const atalhos = atalhosDeBusca(pesquisas);

  const pesqA = pesquisas.find((p) => p.id === idA) || null;
  const pesqB = pesquisas.find((p) => p.id === idB) || null;
  const compat = pesqA && pesqB && pesqA.id !== pesqB.id ? avaliarCompatibilidade(pesqA, pesqB) : null;
  const linhas = compat?.comparavel && pesqA && pesqB ? compararResultados(pesqA, pesqB) : [];

  const institutos = new Set(pesquisas.map((p) => p.instituto));
  const comRegistro = pesquisas.filter((p) => p.registro_tse).length;
  const amostraMedia = pesquisas.length
    ? Math.round(pesquisas.reduce((t, p) => t + (p.amostra || 0), 0) / pesquisas.length)
    : 0;

  // Série por candidato ao longo das pesquisas, da mais antiga para a mais nova
  const serie = [...evolucao].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const nomesNaSerie = [...new Set(serie.flatMap((e) => Object.keys(e.resultados || {})))];

  return (
    <div className="page" id="p-pesquisas">
      <div className="head">
        <div>
          <h1 style={{ color: 'var(--eleg)' }}>Pesquisas</h1>
          <p>
            {/*
              proto:988 diz "sinaliza confiança", que soaria a cálculo:
              `eleg.pesquisa.confianca` é enum digitado na ficha
              (alta/media/baixa). O resto é literal e verdadeiro — os cinco
              campos são colunas reais, e `pesquisas/comparacao.ts` bloqueia
              mesmo o delta quando abrangência, metodologia ou cenário divergem.
            */}
            Cada pesquisa é uma fotografia histórica com instituto, período, metodologia, amostra e
            abrangência. O sistema só compara recortes compatíveis; a confiança é a leitura da
            equipe sobre cada levantamento.
          </p>
        </div>
        <div className="act">
          <button
            className="btn"
            onClick={() =>
              exportarCsv(
                'pesquisas',
                [
                  { cabecalho: 'Instituto', valor: (p: any) => p.instituto },
                  { cabecalho: 'Período', valor: (p: any) => p.periodo },
                  { cabecalho: 'Abrangência', valor: (p: any) => p.abrangencia },
                  { cabecalho: 'Cenário', valor: (p: any) => p.cenario ?? '' },
                  { cabecalho: 'Metodologia', valor: (p: any) => p.metodologia },
                  { cabecalho: 'Amostra', valor: (p: any) => p.amostra },
                  { cabecalho: 'Confiança', valor: (p: any) => p.confianca },
                  { cabecalho: 'Registro TSE', valor: (p: any) => p.registro_tse ?? '' },
                  { cabecalho: 'Origem', valor: (p: any) => p.origem },
                ],
                pesquisas,
              )
            }
            disabled={pesquisas.length === 0}
            title="Exporta as pesquisas cadastradas"
          >
            Exportar
          </button>
          <button className="btn pri" onClick={() => setModal('nova')}>
            + Nova pesquisa
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Pesquisas cadastradas</div>
          <div className="v">{pesquisas.length}</div>
          <div className="d">{institutos.size} instituto(s)</div>
        </div>
        <div className="card kpi">
          <div className="l">Com registro no TSE</div>
          <div className="v" style={{ color: comRegistro === pesquisas.length ? 'var(--good)' : 'var(--warn)' }}>
            {comRegistro}
          </div>
          <div className="d">de {pesquisas.length} cadastradas</div>
        </div>
        <div className="card kpi">
          <div className="l">Amostra média</div>
          <div className="v">{amostraMedia.toLocaleString('pt-BR')}</div>
          <div className="d">entrevistados por levantamento</div>
        </div>
        <div className="card kpi">
          <div className="l">Alta confiança</div>
          <div className="v" style={{ color: 'var(--good)' }}>
            {pesquisas.filter((p) => p.confianca === 'alta').length}
          </div>
          <div className="d">segundo a avaliação da equipe</div>
        </div>
      </div>

      {/* Comparativo A/B com guarda de compatibilidade metodológica */}
      <div className="card" style={{ marginTop: 12 }} data-testid="comparativo">
        <h2>Comparar duas pesquisas</h2>
        <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 12px' }}>
          A variação é sempre B − A, em pontos percentuais.{' '}
          <Info titulo="O que o sistema confere antes de calcular a variação">
            Antes de calcular, o sistema confere se os dois levantamentos têm a mesma abrangência,
            a mesma metodologia e o mesmo cenário, e declara abaixo o que ele não tem como
            conferir.
          </Info>
        </p>

        <div className="grid g2" style={{ marginBottom: 12 }}>
          <div className="field">
            <label htmlFor="cmp-a">Pesquisa A</label>
            <select
              id="cmp-a"
              className="in"
              value={idA}
              onChange={(e) => setIdA(e.target.value)}
              disabled={pesquisas.length < 2}
            >
              <option value="">— selecione</option>
              {pesquisas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.instituto} · {p.periodo}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cmp-b">Pesquisa B</label>
            <select
              id="cmp-b"
              className="in"
              value={idB}
              onChange={(e) => setIdB(e.target.value)}
              disabled={pesquisas.length < 2}
            >
              <option value="">— selecione</option>
              {pesquisas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.instituto} · {p.periodo}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!compat && (
          <div className="note" style={{ fontSize: 12 }}>
            {pesquisas.length < 2
              ? 'É preciso ter ao menos duas pesquisas cadastradas para comparar.'
              : 'Escolha duas pesquisas diferentes em A e B.'}
          </div>
        )}

        {compat && !compat.comparavel && (
          <div className="note crit" style={{ fontSize: 13 }}>
            <b>Estas duas pesquisas não são comparáveis.</b>
            <div style={{ marginTop: 6 }}>
              A comparação foi bloqueada porque{' '}
              {compat.incompativeis.length === 1 ? 'esta dimensão diverge' : 'estas dimensões divergem'}:
            </div>
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              {compat.incompativeis.map((d) => (
                <li key={d.rotulo} style={{ marginBottom: 4 }}>
                  <b>{d.rotulo}</b> · A: “{d.a}” · B: “{d.b}”
                </li>
              ))}
            </ul>
            <div style={{ marginTop: 8, color: 'var(--ink2)' }}>
              Um delta entre recortes diferentes seria um número correto com o rótulo errado. Escolha
              dois levantamentos do mesmo recorte.
            </div>
          </div>
        )}

        {compat?.comparavel && (
          <>
            <div className="tw">
              <table data-testid="tabela-comparativo">
                <thead>
                  <tr>
                    <th>Item perguntado</th>
                    <th style={{ textAlign: 'right' }}>A</th>
                    <th style={{ textAlign: 'right' }}>B</th>
                    <th style={{ textAlign: 'right' }}>Variação (p.p.)</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((l) => (
                    <tr key={l.nome} data-testid={`cmp-${l.nome}`}>
                      <td>{l.nome}</td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {l.va === null ? (
                          <span style={{ color: 'var(--ink3)' }}>não perguntado</span>
                        ) : (
                          `${pct(l.va)}%`
                        )}
                      </td>
                      <td className="num" style={{ textAlign: 'right' }}>
                        {l.vb === null ? (
                          <span style={{ color: 'var(--ink3)' }}>não perguntado</span>
                        ) : (
                          `${pct(l.vb)}%`
                        )}
                      </td>
                      <td
                        className="num"
                        style={{
                          textAlign: 'right',
                          color:
                            l.delta === null
                              ? 'var(--ink3)'
                              : l.delta > 0
                                ? 'var(--good)'
                                : l.delta < 0
                                  ? 'var(--crit)'
                                  : 'inherit',
                        }}
                      >
                        {l.delta === null ? 'sem variação comparável' : sinal(l.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="note warn" style={{ marginTop: 10, fontSize: 12 }}>
              <b>Verificação parcial.</b> Estas dimensões não dá para conferir aqui:{' '}
              <Info titulo="O que o sistema conseguiu conferir">
                Abrangência, metodologia e cenário: as três dimensões que o cadastro guarda.
              </Info>
              <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                {compat.naoVerificaveis.map((t) => (
                  <li key={t} style={{ marginBottom: 4 }}>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Evolução */}
      {serie.length > 1 && (
        <div className="card" style={{ marginTop: 12 }}>
          <h2>Evolução entre pesquisas</h2>
          <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 14px' }}>
            Cada ponto é um levantamento, ordenado por data de cadastro.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {nomesNaSerie.map((nome, i) => (
              <div key={nome}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <b style={{ color: CORES[i % CORES.length] }}>{nome}</b>
                  <span style={{ color: 'var(--ink3)', fontSize: 12 }}>
                    {serie.map((s) => `${s.resultados?.[nome] ?? 0}%`).join(' → ')}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 60 }}>
                  {serie.map((s) => {
                    const v = s.resultados?.[nome] ?? 0;
                    return (
                      <div key={s.id} style={{ flex: 1, textAlign: 'center' }}>
                        <div
                          title={`${s.instituto} · ${s.periodo}: ${v}%`}
                          style={{
                            height: `${Math.max(2, v)}%`,
                            background: CORES[i % CORES.length],
                            borderRadius: '3px 3px 0 0',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Busca livre + atalhos */}
      <div className="card flat" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="search"
            className="in"
            style={{ flex: 1 }}
            aria-label="Buscar pesquisas"
            placeholder="Buscar por qualquer palavra: instituto, período, metodologia, abrangência, cenário, registro TSE, nome perguntado…"
            value={consulta}
            onChange={(e) => setConsulta(e.target.value)}
          />
          <span
            data-testid="pq-contagem"
            style={{ fontSize: 12, color: 'var(--ink3)', whiteSpace: 'nowrap' }}
          >
            {visiveis.length} de {pesquisas.length} pesquisas
          </span>
          <button className="btn sm" onClick={() => setConsulta('')} disabled={!consulta}>
            Limpar
          </button>
        </div>

        {atalhos.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {atalhos.map((a) => (
              <button
                key={a.termo}
                type="button"
                className="chip neutral"
                style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'none' }}
                onClick={() => setConsulta(a.termo)}
              >
                {a.termo} · {a.n}
              </button>
            ))}
          </div>
        )}

        <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '8px 0 0' }}>
          A busca é local: procura dentro das {pesquisas.length} pesquisas já carregadas, e o que
          não estiver cadastrado não aparece aqui.{' '}
          <Info titulo="Por que a busca não consulta o servidor">
            A API de pesquisas não aceita filtro de texto. Os atalhos acima saem dos próprios
            valores cadastrados (instituto, abrangência e cenário), com a quantidade de pesquisas
            que cada um traz.
          </Info>
        </p>
      </div>

      {/* Lista */}
      <div className="card flat tw" style={{ marginTop: 12 }} data-testid="tabela-pesquisas">
        <table>
          <thead>
            <tr>
              <th>Instituto / período</th>
              <th>Abrangência</th>
              <th>Cenário</th>
              <th style={{ textAlign: 'right' }}>Amostra</th>
              <th>Registro TSE</th>
              <th>Confiança</th>
              <th>Origem</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((p) => (
              <tr key={p.id} className="row" style={{ cursor: 'pointer' }} onClick={() => setSelecionada(p)}>
                <td>
                  <b>{p.instituto}</b>
                  <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{p.periodo}</div>
                </td>
                <td>{p.abrangencia}</td>
                <td>{p.cenario}</td>
                <td className="num" style={{ textAlign: 'right' }}>
                  {p.amostra?.toLocaleString('pt-BR')}
                </td>
                <td className="mono" style={{ fontSize: 12 }}>
                  {p.registro_tse || (
                    <span className="chip warn" style={{ fontSize: 10 }}>
                      sem registro
                    </span>
                  )}
                </td>
                <td>
                  <span className={`chip ${chipConfianca(p.confianca)}`}>{p.confianca}</span>
                </td>
                <td>
                  <span className="chip neutral">
                    {p.origem === 'ia_jornal' ? 'imprensa' : 'gabinete'}
                  </span>
                </td>
              </tr>
            ))}

            {isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Carregando pesquisas...
                </td>
              </tr>
            )}
            {!isLoading && pesquisas.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  Nenhuma pesquisa cadastrada ainda.
                </td>
              </tr>
            )}
            {!isLoading && pesquisas.length > 0 && visiveis.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  Nenhuma pesquisa com essas palavras. Tente instituto, período, metodologia,
                  abrangência, cenário ou o nome de quem foi perguntado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="note" style={{ marginTop: 12, fontSize: 12 }}>
        <b>Sobre a origem:</b> a plataforma não varre a imprensa. Toda pesquisa aqui foi cadastrada
        por alguém da equipe, que também classificou a confiança.
      </div>

      {/* Drawer: detalhe */}
      {selecionada && !modal && (
        <Drawer
          aberto
          titulo={selecionada.instituto}
          aoFechar={() => setSelecionada(null)}
        >
          <div className="filters" style={{ marginBottom: 12 }}>
            <span className={`chip ${chipConfianca(selecionada.confianca)}`}>
              confiança {selecionada.confianca}
            </span>
            <span className="chip neutral">{selecionada.cenario}</span>
          </div>

          <dl className="dl">
            <dt>Período</dt>
            <dd>{selecionada.periodo}</dd>
            <dt>Abrangência</dt>
            <dd>{selecionada.abrangencia}</dd>
            <dt>Metodologia</dt>
            <dd>{selecionada.metodologia}</dd>
            <dt>Amostra</dt>
            <dd className="num">{selecionada.amostra?.toLocaleString('pt-BR')} entrevistados</dd>
            <dt>Registro TSE</dt>
            <dd className="mono">{selecionada.registro_tse || 'não informado'}</dd>
            <dt>Fonte</dt>
            <dd>
              {selecionada.fonte_url ? (
                <a
                  href={selecionada.fonte_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--accent)' }}
                >
                  abrir original
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt>Cadastrada em</dt>
            <dd>{formatarData(selecionada.created_at)}</dd>
          </dl>

          <h3 style={{ marginTop: 16 }}>Resultados</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {Object.entries(selecionada.resultados || {})
              .sort((a, b) => b[1] - a[1])
              .map(([nome, valor], i) => (
                <div key={nome}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{nome}</span>
                    <b className="num">{valor}%</b>
                  </div>
                  <div className="bar" style={{ marginTop: 3 }}>
                    <i style={{ width: `${valor}%`, background: CORES[i % CORES.length] }} />
                  </div>
                </div>
              ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button className="btn" onClick={() => setModal('editar')}>
              ✏️ Editar
            </button>
            <button
              className="btn"
              style={{ color: 'var(--crit)', marginLeft: 'auto' }}
              disabled={excluir.isPending}
              onClick={() => {
                if (window.confirm(`Remover a pesquisa do ${selecionada.instituto}?`)) {
                  excluir.mutate(selecionada.id);
                }
              }}
            >
              Excluir
            </button>
          </div>
        </Drawer>
      )}

      {modal && (
        <ModalPesquisa
          pesquisa={modal === 'editar' ? selecionada : null}
          sugestoes={candidatos.map((c) => c.nome)}
          salvando={criar.isPending || atualizar.isPending}
          onCancelar={() => setModal(null)}
          onSalvar={(dados) => {
            if (modal === 'nova') criar.mutate(dados);
            else if (selecionada) atualizar.mutate({ id: selecionada.id, dados });
          }}
        />
      )}
    </div>
  );
}

function ModalPesquisa({
  pesquisa,
  sugestoes,
  salvando,
  onCancelar,
  onSalvar,
}: {
  pesquisa: Pesquisa | null;
  sugestoes: string[];
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: any) => void;
}) {
  const [instituto, setInstituto] = useState(pesquisa?.instituto || '');
  const [periodo, setPeriodo] = useState(pesquisa?.periodo || '');
  const [metodologia, setMetodologia] = useState(pesquisa?.metodologia || 'Entrevistas presenciais');
  const [amostra, setAmostra] = useState(pesquisa?.amostra ?? 800);
  const [abrangencia, setAbrangencia] = useState(pesquisa?.abrangencia || 'Município de Fortaleza');
  const [cenario, setCenario] = useState(pesquisa?.cenario || 'Estimulada');
  const [registroTse, setRegistroTse] = useState(pesquisa?.registro_tse || '');
  const [confianca, setConfianca] = useState<'alta' | 'media' | 'baixa'>(
    pesquisa?.confianca || 'media',
  );
  const [fonteUrl, setFonteUrl] = useState(pesquisa?.fonte_url || '');

  const [resultados, setResultados] = useState<Resultado[]>(() =>
    Object.entries(pesquisa?.resultados || {}).map(([nome, valor]) => ({ nome, valor })),
  );

  const totalPct = resultados.reduce((t, r) => t + (Number(r.valor) || 0), 0);

  const alterar = (i: number, campo: 'nome' | 'valor', v: any) =>
    setResultados((prev) => prev.map((r, idx) => (idx === i ? { ...r, [campo]: v } : r)));

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!instituto.trim() || !periodo.trim()) {
      toast('Instituto e período são obrigatórios.');
      return;
    }
    const validos = resultados.filter((r) => r.nome.trim());
    if (validos.length === 0) {
      toast('Informe ao menos um resultado.');
      return;
    }
    if (totalPct > 100.5) {
      toast(`A soma dos resultados é ${totalPct.toFixed(1)}%. Verifique os percentuais.`);
      return;
    }

    onSalvar({
      instituto: instituto.trim(),
      periodo: periodo.trim(),
      metodologia: metodologia.trim(),
      amostra,
      abrangencia: abrangencia.trim(),
      cenario: cenario.trim(),
      registro_tse: registroTse.trim() || undefined,
      confianca,
      fonte_url: fonteUrl.trim() || undefined,
      resultados: Object.fromEntries(validos.map((r) => [r.nome.trim(), Number(r.valor) || 0])),
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={pesquisa ? 'Editar pesquisa' : 'Nova pesquisa eleitoral'}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : pesquisa ? 'Salvar alterações' : 'Cadastrar pesquisa'}
            </button>
          </>
        }
      >
        <div className="grid g2">
          <div className="field">
            <label>Instituto *</label>
            <input
              className="in"
              value={instituto}
              onChange={(e) => setInstituto(e.target.value)}
              placeholder="Ex: Datafolha"
              required
            />
          </div>
          <div className="field">
            <label>Período *</label>
            <input
              className="in"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              placeholder="Ex: 10 a 12 de agosto de 2026"
              required
            />
          </div>
        </div>

        <div className="grid g2">
          <div className="field">
            <label>Abrangência</label>
            <input
              className="in"
              value={abrangencia}
              onChange={(e) => setAbrangencia(e.target.value)}
            />
          </div>
          <div className="field">
            <label>Cenário</label>
            <input className="in" value={cenario} onChange={(e) => setCenario(e.target.value)} />
          </div>
        </div>

        <div className="grid g3">
          <div className="field">
            <label>Amostra</label>
            <input
              type="number"
              min={1}
              className="in num"
              value={amostra}
              onChange={(e) => setAmostra(Number(e.target.value) || 0)}
            />
          </div>
          <div className="field">
            <label>Registro TSE</label>
            <input
              className="in mono"
              value={registroTse}
              onChange={(e) => setRegistroTse(e.target.value)}
              placeholder="CE-00123/2026"
            />
          </div>
          <div className="field">
            <label>Confiança da equipe</label>
            <select
              className="in"
              value={confianca}
              onChange={(e) => setConfianca(e.target.value as any)}
            >
              {CONFIANCA.map((c) => (
                <option key={c.v} value={c.v}>
                  {c.l}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Metodologia</label>
          <input
            className="in"
            value={metodologia}
            onChange={(e) => setMetodologia(e.target.value)}
            placeholder="Ex: Entrevistas presenciais com questionário estruturado"
          />
        </div>

        <div className="field">
          <label>URL da fonte</label>
          <input
            className="in"
            type="url"
            value={fonteUrl}
            onChange={(e) => setFonteUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        {/* Resultados */}
        <div className="field">
          <label>
            Resultados{' '}
            <span
              style={{
                fontSize: 11,
                color: totalPct > 100.5 ? 'var(--crit)' : 'var(--ink3)',
                fontWeight: 400,
              }}
            >
              · soma atual: {totalPct.toFixed(1)}%
            </span>
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {resultados.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <input
                  className="in sm"
                  list="nomes-pesquisa"
                  style={{ flex: 1 }}
                  value={r.nome}
                  onChange={(e) => alterar(i, 'nome', e.target.value)}
                  placeholder="Candidato, brancos, nulos, indecisos..."
                />
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  className="in sm num"
                  style={{ width: 90 }}
                  value={r.valor}
                  onChange={(e) => alterar(i, 'valor', Number(e.target.value) || 0)}
                />
                <button
                  type="button"
                  className="btn sm"
                  style={{ color: 'var(--crit)' }}
                  onClick={() => setResultados(resultados.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              </div>
            ))}
            <datalist id="nomes-pesquisa">
              {[...sugestoes, 'Brancos e nulos', 'Indecisos'].map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>

            <button
              type="button"
              className="btn sm"
              onClick={() => setResultados([...resultados, { nome: '', valor: 0 }])}
            >
              + Adicionar linha
            </button>
          </div>
        </div>

      </Drawer>
    </form>
  );
}
